import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Image, Form } from "react-bootstrap";
import { Pagination, PaginationProps } from "antd";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/missedscreenshots.scss";

import { commonGetService } from "../../utils/properties";
import Sorter from "../Employees/EmployeePerformance/Sorter";

type SystemStatus = "Online" | "Offline" | "Yet to Track";

interface MissedScreenshotItem {
  employee_id: string;
  employee_name: string;
  employee_mail: string;
  employee_image?: string;
  department: string;
  designation: string;
  system_status: SystemStatus | string;
  emp_status?: string;
  last_screenshot_capture?: string;
  last_screenshot_capture_ago?: string;
}

interface FilterOption {
  filterValue: string;
  isSelect: boolean;
  value?: string;
}

type FilterId = "department" | "designation" | "systemStatus";

type FilterState = Record<FilterId, FilterOption[]>;

type SortOrder = "asc" | "desc";

interface MissedScreenshotApiData {
  content?: MissedScreenshotItem[];
  totalElements?: number;
  totalPages?: number;
  number?: number;
  size?: number;
  empty?: boolean;
}


const STATIC_STATUS_OPTIONS: FilterOption[] = [
  { filterValue: "Online", isSelect: false, value: "Online" },
  { filterValue: "Offline", isSelect: false, value: "Offline" },
  { filterValue: "Yet to Track", isSelect: false, value: "Yet to Track" },
];

const FILTER_FIELDS: { id: FilterId; label: string; placeholder: string }[] = [
  { id: "department", label: "Department", placeholder: "Select Department" },
  { id: "designation", label: "Designation", placeholder: "Select Designation" },
  { id: "systemStatus", label: "System Status", placeholder: "Select Status" },
];

const DEPARTMENTS_ENDPOINT = "/api/1.0/role/departments";
const DESIGNATIONS_ENDPOINT = "/api/1.0/employee/designations";
const SCREENSHOTS_ENDPOINT = "/api/1.0/screenshot/missed-screenshot";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const EXPORT_HEADERS = [
  "Emp ID",
  "Employee",
  "Department",
  "Designation",
  "Last Screenshot Capture",
  "System Status",
];

const getStatusMeta = (status?: string) => {
  const key = (status || "").toLowerCase();

  if (key === "online") {
    return { label: "Online", className: "status-dot status-online" };
  }

  if (key === "offline") {
    return { label: "Offline", className: "status-dot status-offline" };
  }

  return {
    label: status || "Yet to Track",
    className: "status-dot status-pending",
  };
};

const parseFilterDate = (value?: string): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTimeForApi = (date: Date | null): string => {
  if (!date) {
    return "";
  }

  const pad = (value: number) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatDateTimeLabel = (value: string) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const isSameDay = (firstDate: Date, secondDate: Date) => {
  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate()
  );
};

const getStartOfDay = (date: Date) => {
  const start = new Date(date);

  start.setHours(0, 0, 0, 0);

  return start;
};

const getEndOfDay = (date: Date) => {
  const end = new Date(date);

  end.setHours(23, 59, 59, 999);

  return end;
};

const getMaxTime = (selectedDate: Date | null) => {
  const now = new Date();

  if (!selectedDate) {
    return getEndOfDay(now);
  }

  return isSameDay(selectedDate, now) ? now : getEndOfDay(selectedDate);
};

const isSelectableTime = (time: Date, selectedDate: Date | null) => {
  if (!selectedDate) {
    return true;
  }

  const now = new Date();

  if (!isSameDay(selectedDate, now)) {
    return true;
  }

  return time.getTime() <= now.getTime();
};



const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const renderDatePickerHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
}) => {
  return (
    <div className="capture-date-picker-header">
      <button
        type="button"
        className="datepicker-header-arrow button-left"
        onClick={(event) => {
          event.stopPropagation();
          decreaseMonth();
        }}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
      >
        <Image src="/images/dashboard/LeftArrow.svg" alt="Previous" />
      </button>

      <h6>
        {MONTH_NAMES[date.getMonth()]} {date.getFullYear()}
      </h6>

      <button
        type="button"
        className="datepicker-header-arrow button-right"
        onClick={(event) => {
          event.stopPropagation();
          increaseMonth();
        }}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
      >
        <Image src="/images/dashboard/rightArrow.svg" alt="Next" />
      </button>
    </div>
  );
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebounced(value);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delayMs]);

  return debounced;
}

function exportToCsv(rows: MissedScreenshotItem[], filename: string) {
  if (!rows.length) {
    return;
  }

  // Convert values to plain CSV text.
  const cleanCell = (cell: string) => {
    return String(cell ?? "")
      .replace(/"/g, "")
      .replace(/[\r\n]+/g, " ")
      .trim();
  };

  const formatScreenshotTime = (value?: string) => {
    if (!value) {
      return "-";
    }

    return String(value)
      .replace(/,\s*/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const csvRows = rows.map((item) =>
    [
      cleanCell(item.employee_id),
      cleanCell(item.employee_name),
      cleanCell(item.department),
      cleanCell(item.designation),
      formatScreenshotTime(item.last_screenshot_capture),
      cleanCell(getStatusMeta(item.system_status).label),
    ].join(",")
  );

  const csvContent = [
    EXPORT_HEADERS.map(cleanCell).join(","),
    ...csvRows,
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");

  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

const CheckIcon = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
    <path
      d="M1 4L3.5 6.5L9 1"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function CheckboxRow({
  option,
  checked,
  onToggle,
}: {
  option: FilterOption;
  checked: boolean;
  onToggle: (filterValue: string) => void;
}) {
  return (
    <label className={`dropdown-checkbox-row${checked ? " checked" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(option.filterValue)}
        hidden
      />

      <span className={`custom-checkbox${checked ? " checked" : ""}`}>
        {checked && <CheckIcon />}
      </span>

      <span className="dropdown-checkbox-label">{option.filterValue}</span>
    </label>
  );
}

const InlineFilter = memo(function InlineFilter({
  id,
  label,
  placeholder,
  options,
  onApply,
  isOpen,
  onToggle,
}: {
  id: FilterId;
  label: string;
  placeholder: string;
  options: FilterOption[];
  onApply: (id: FilterId, options: FilterOption[]) => void;
  isOpen: boolean;
  onToggle: (id: FilterId) => void;
}) {
  const [temp, setTemp] = useState<FilterOption[]>(options);

  const selectedCount = options.filter((option) => option.isSelect).length;

  useEffect(() => {
    if (isOpen) {
      setTemp(options.map((option) => ({ ...option })));
    }
  }, [isOpen, options]);

  // O(1) membership lookup instead of `Array.find` per option per render.
  const tempSelectedSet = useMemo(
    () =>
      new Set(
        temp.filter((option) => option.isSelect).map((option) => option.filterValue)
      ),
    [temp]
  );

  const toggleOption = (filterValue: string) => {
    setTemp((previous) =>
      previous.map((option) =>
        option.filterValue === filterValue
          ? { ...option, isSelect: !option.isSelect }
          : option
      )
    );
  };

  const handleReset = () => {
    setTemp((previous) =>
      previous.map((option) => ({ ...option, isSelect: false }))
    );
  };

  return (
    <div className="inline-filter">
      <button
        type="button"
        className={`inline-filter-trigger${selectedCount ? " active" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => onToggle(id)}
      >
        <span className="inline-filter-label">{label} :</span>

        <span className="inline-filter-value">
          {selectedCount ? `${selectedCount} selected` : placeholder}

          <Image
            src="/images/dashboard/downarrow.svg"
            alt=""
            aria-hidden="true"
          />
        </span>
      </button>

      {isOpen && (
        <div className="inline-filter-panel" role="listbox">
          <div className="inline-filter-options">
            {options.length === 0 ? (
              <p className="no-options-text">No options available</p>
            ) : (
              options.map((option) => (
                <CheckboxRow
                  key={option.filterValue}
                  option={option}
                  checked={tempSelectedSet.has(option.filterValue)}
                  onToggle={toggleOption}
                />
              ))
            )}
          </div>

          <div className="inline-filter-actions">
            <button
              type="button"
              className="inline-filter-clear"
              onClick={handleReset}
            >
              Reset
            </button>

            <button
              type="button"
              className="inline-filter-apply"
              onClick={() => onApply(id, temp)}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

function ModalFilterField({
  label,
  placeholder,
  options,
  isOpen,
  onToggle,
  onToggleOption,
  onRemoveChip,
}: {
  label: string;
  placeholder: string;
  options: FilterOption[];
  isOpen: boolean;
  onToggle: () => void;
  onToggleOption: (filterValue: string) => void;
  onRemoveChip: (filterValue: string) => void;
}) {
  const selected = options.filter((option) => option.isSelect);

  const visibleChips = selected.slice(0, 2);

  const extraCount = selected.length - visibleChips.length;

  return (
    <div className="modal-filter-field">
      <label className="modal-filter-label">{label}</label>

      <div
        className={`modal-filter-select-wrapper${isOpen ? " is-open" : ""}`}
      >
        <button
          type="button"
          className="modal-filter-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          {selected.length === 0 ? (
            <span className="modal-filter-placeholder">{placeholder}</span>
          ) : (
            <span className="modal-filter-chips">
              {visibleChips.map((option) => (
                <span key={option.filterValue} className="modal-filter-chip">
                  {option.filterValue}

                  <span
                    className="modal-filter-chip-remove"
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();

                      onRemoveChip(option.filterValue);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();

                        onRemoveChip(option.filterValue);
                      }
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}

              {extraCount > 0 && (
                <span className="modal-filter-chip-more">+{extraCount}</span>
              )}
            </span>
          )}

          <svg
            className="modal-filter-arrow"
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1 1L5 5L9 1"
              stroke="#6D6D6D"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="modal-filter-panel" role="listbox">
            {options.length === 0 ? (
              <p className="no-options-text">No options available</p>
            ) : (
              options.map((option) => (
                <CheckboxRow
                  key={option.filterValue}
                  option={option}
                  checked={option.isSelect}
                  onToggle={onToggleOption}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const selectedValuesOf = (options: FilterOption[]) =>
  options
    .filter((option) => option.isSelect)
    .map((option) => option.value ?? option.filterValue);

const MissedScreenshots = () => {
  const modalCaptureDatePickerRef = useRef<DatePicker | null>(null);

  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  const [openDropdown, setOpenDropdown] = useState<FilterId | null>(null);

  const [filterOptions, setFilterOptions] = useState<FilterState>({
    department: [],
    designation: [],
    systemStatus: STATIC_STATUS_OPTIONS,
  });

  const [appliedFilters, setAppliedFilters] = useState<
    Record<FilterId, string[]>
  >({
    department: [],
    designation: [],
    systemStatus: [],
  });

  const [captureLessThan, setCaptureLessThan] = useState("");
  const [tempCaptureLessThan, setTempCaptureLessThan] = useState("");

  const [pendingCaptureDate, setPendingCaptureDate] = useState<Date | null>(
    null
  );

  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const [rows, setRows] = useState<MissedScreenshotItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [current, setCurrent] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [modalOpenDropdown, setModalOpenDropdown] =
    useState<FilterId | null>(null);

  const [modalFilters, setModalFilters] = useState<FilterState>({
    department: [],
    designation: [],
    systemStatus: [],
  });

  const parsedCaptureLessThan = useMemo(
    () => parseFilterDate(captureLessThan),
    [captureLessThan]
  );

  const parsedTempCaptureLessThan = useMemo(
    () => parseFilterDate(tempCaptureLessThan),
    [tempCaptureLessThan]
  );

  const handleDatePickerChange = useCallback(
    (
      date: Date | null,
      setter: React.Dispatch<React.SetStateAction<string>>,
      resetPage = false
    ) => {
      if (!date) {
        setter("");

        if (resetPage) {
          setCurrent(1);
        }

        return;
      }

      const now = new Date();

      if (date.getTime() > now.getTime()) {
        return;
      }

      setter(formatDateTimeForApi(date));

      if (resetPage) {
        setCurrent(1);
      }
    },
    []
  );

  const handleCaptureFilterChange = useCallback((date: Date | null) => {
    if (!date) {
      setPendingCaptureDate(null);
      setCaptureLessThan("");
      setCurrent(1);
      return;
    }

    const now = new Date();

    if (date.getTime() > now.getTime()) {
      return;
    }
    setPendingCaptureDate(date);
    if (
      date.getHours() !== 0 ||
      date.getMinutes() !== 0 ||
      date.getSeconds() !== 0
    ) {
      setCaptureLessThan(formatDateTimeForApi(date));
      setCurrent(1);
    }
  }, []);

  const handleTempCaptureFilterChange = useCallback(
    (date: Date | null) => {
      handleDatePickerChange(date, setTempCaptureLessThan);
    },
    [handleDatePickerChange]
  );

  useEffect(() => {
    const controller = new AbortController();

    const loadOptions = (
      endpoint: string,
      id: "department" | "designation"
    ) => {
      commonGetService(endpoint, controller.signal).then((res) => {
        if (controller.signal.aborted || res.status === "cancelled") {
          return;
        }

        if (res.status === 200) {
          const apiOptions = Array.isArray(res.data)
            ? res.data
            : res.data?.data ?? [];

          const options: FilterOption[] = apiOptions.map((item: any) => ({
            filterValue: item.label ?? item.name ?? item.value ?? "",
            isSelect: false,
            value: item.value ?? item.id ?? item.label ?? "",
          }));

          setFilterOptions((previous) => ({ ...previous, [id]: options }));
        } else {
          setFilterOptions((previous) => ({ ...previous, [id]: [] }));
        }
      });
    };

    loadOptions(DEPARTMENTS_ENDPOINT, "department");
    loadOptions(DESIGNATIONS_ENDPOINT, "designation");

    return () => {
      controller.abort();
    };
  }, []);


  useEffect(() => {
    const controller = new AbortController();

    const page = Math.max(current - 1, 0);

    const params = new URLSearchParams();

    params.set("page", String(page));
    params.set("size", String(PAGE_SIZE));

    if (appliedFilters.department.length) {
      params.set("department_ids", appliedFilters.department.join(","));
    }

    if (appliedFilters.designation.length) {
      params.set("designation_ids", appliedFilters.designation.join(","));
    }

    if (appliedFilters.systemStatus.length) {
      params.set("current_status", appliedFilters.systemStatus.join(","));
    }

    if (debouncedSearch.trim()) {
      params.set("search", debouncedSearch.trim());
    }

    if (captureLessThan) {
      params.set(
        "last_screenshot_before",
        captureLessThan
      );
    }
    params.set("sort_direction", sortOrder);

    commonGetService(
      `${SCREENSHOTS_ENDPOINT}?${params.toString()}`,
      controller.signal
    ).then((res) => {
      if (controller.signal.aborted || res.status === "cancelled") {
        return;
      }

      if (res.status === 200) {
        const responseData: MissedScreenshotApiData = res.data?.content
          ? res.data
          : res.data?.data ?? {};

        const content = Array.isArray(responseData.content)
          ? responseData.content
          : [];

        setRows(content);
        setTotalElements(Number(responseData.totalElements) || 0);
        setIsLoading(false);
      } else {
        setRows([]);
        setTotalElements(0);
        setIsLoading(false);
      }
    });

    return () => {
      controller.abort();
    };
  }, [appliedFilters, debouncedSearch, captureLessThan, sortOrder, current]);


  useEffect(() => {
    setCurrent(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!openDropdown) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;

      if (target?.closest(".inline-filter")) {
        return;
      }

      setOpenDropdown(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [openDropdown]);

  const handleToggleDropdown = useCallback((id: FilterId) => {
    setOpenDropdown((previous) => (previous === id ? null : id));
  }, []);

  const handleApplyFilter = useCallback(
    (id: FilterId, options: FilterOption[]) => {
      setFilterOptions((previous) => ({ ...previous, [id]: options }));

      setAppliedFilters((previous) => ({
        ...previous,
        [id]: selectedValuesOf(options),
      }));

      setOpenDropdown(null);
      setCurrent(1);
    },
    []
  );

  const hasActiveFilters = useMemo(
    () =>
      Object.values(appliedFilters).some((values) => values.length > 0) ||
      Boolean(captureLessThan),
    [appliedFilters, captureLessThan]
  );

  const handleSortCapture = useCallback((order: string) => {
    if (order !== "asc" && order !== "desc") {
      return;
    }

    setSortOrder(order as SortOrder);
    setCurrent(1);
  }, []);

  const handleClearAll = useCallback(() => {
    setFilterOptions((previous) => {
      const cleared = {} as FilterState;

      (Object.keys(previous) as FilterId[]).forEach((id) => {
        cleared[id] = previous[id].map((option) => ({
          ...option,
          isSelect: false,
        }));
      });

      return cleared;
    });

    setAppliedFilters({ department: [], designation: [], systemStatus: [] });
    setCaptureLessThan("");
    setPendingCaptureDate(null);
    setTempCaptureLessThan("");
    setSortOrder("asc");
    setCurrent(1);
  }, []);

  const handleOpenFilterModal = useCallback(() => {
    setModalFilters({
      department: filterOptions.department.map((option) => ({ ...option })),
      designation: filterOptions.designation.map((option) => ({ ...option })),
      systemStatus: filterOptions.systemStatus.map((option) => ({
        ...option,
      })),

    });

    setTempCaptureLessThan(captureLessThan);
    setModalOpenDropdown(null);
    setShowFilterModal(true);
    setPendingCaptureDate(parsedCaptureLessThan);
  }, [filterOptions, captureLessThan]);

  const handleCloseFilterModal = useCallback(() => {
    setShowFilterModal(false);
    setModalOpenDropdown(null);
  }, []);

  const handleModalToggleDropdown = useCallback((id: FilterId) => {
    setModalOpenDropdown((previous) => (previous === id ? null : id));
  }, []);

  useEffect(() => {
    if (!showFilterModal || !modalOpenDropdown) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;

      if (!target?.closest(".modal-filter-select-wrapper")) {
        setModalOpenDropdown(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [showFilterModal, modalOpenDropdown]);

  const handleModalToggleOption = useCallback(
    (id: FilterId, filterValue: string) => {
      setModalFilters((previous) => ({
        ...previous,
        [id]: previous[id].map((option) =>
          option.filterValue === filterValue
            ? { ...option, isSelect: !option.isSelect }
            : option
        ),
      }));
    },
    []
  );

  const handleModalRemoveChip = useCallback(
    (id: FilterId, filterValue: string) => {
      setModalFilters((previous) => ({
        ...previous,
        [id]: previous[id].map((option) =>
          option.filterValue === filterValue
            ? { ...option, isSelect: false }
            : option
        ),
      }));
    },
    []
  );

  const handleModalApply = useCallback(() => {
    setFilterOptions(modalFilters);

    setAppliedFilters({
      department: selectedValuesOf(modalFilters.department),
      designation: selectedValuesOf(modalFilters.designation),
      systemStatus: selectedValuesOf(modalFilters.systemStatus),
    });

    setCaptureLessThan(tempCaptureLessThan);
    setShowFilterModal(false);
    setModalOpenDropdown(null);
    setCurrent(1);
  }, [modalFilters, tempCaptureLessThan]);

  const handleExport = useCallback(() => {
    exportToCsv(rows, "missed-screenshot-captures.csv");
  }, [rows]);

  const onPageChange: PaginationProps["onChange"] = (page) => {
    setCurrent(page);
  };

  return (
    <div className="screenshots-page-outer">
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center h-100 w-100 crt-loader">
          <img src="/images/loader/content_loader.gif" alt="Loading" />
        </div>
      ) : (
        <div className="sst-container d-flex flex-column screenshots-page-container new-page">

          <div className="d-flex flex-row justify-content-between align-items-center page-heading">
            <div className="d-flex flex-row align-items-center heading-with-back">
              <img
                src="/images/screenshots/Chevron_left.svg"
                height={25}
                width={25}
                alt=""
              />

              <h4 className="mb-0">
                Employees with missed screenshot captures
              </h4>
            </div>

            <div className="d-flex flex-row align-items-center page-heading-actions">
              <button
                type="button"
                className="tablet-filter-btn"
                aria-label="Open filters"
                onClick={handleOpenFilterModal}
              >
                <Image
                  src="/images/employees/filtericon.svg"
                  alt=""
                  aria-hidden="true"
                  className="tablet-filter-btn-icon"
                />

                <span className="tablet-filter-btn-text">Filter</span>
              </button>

              <button
                type="button"
                className="export-btn"
                onClick={handleExport}
                disabled={!rows.length}
              >
                <Image
                  src="/images/employees/download_icon.svg"
                  alt=""
                  aria-hidden="true"
                />
                Export
              </button>
            </div>
          </div>

          <div className="sst-filters d-flex flex-column">
            <div className="search-bar">
              <Form.Control
                type="text"
                aria-label="Search employees by name, ID, or department"
                placeholder="Search employees by name, ID, or department"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />

              <Image
                src="/images/dashboard/search.svg"
                alt=""
                aria-hidden="true"
                className="position-absolute"
              />
            </div>

            <div className="fltr-fld-cont">
              <div className="fltr-fld">
                {FILTER_FIELDS.map((field) => (
                  <InlineFilter
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    placeholder={field.placeholder}
                    options={filterOptions[field.id]}
                    isOpen={openDropdown === field.id}
                    onToggle={handleToggleDropdown}
                    onApply={handleApplyFilter}
                  />
                ))}

                <div className="capture-date-filter">
                  <div className="capture-date-filter-control">
                    <Image
                      src="/images/dashboard/calendar_icon.svg"
                      alt="Calendar"
                      className="capture-date-calendar-icon"
                    />

                    <DatePicker
                      id="capture-less-than"
                      selected={pendingCaptureDate || parsedCaptureLessThan}
                      onChange={handleCaptureFilterChange}
                      renderCustomHeader={renderDatePickerHeader}
                      showTimeSelect
                      timeIntervals={1}
                      timeCaption="Time"
                      dateFormat="dd-MMM-yyyy h:mm aa"
                      placeholderText="Filter by last capture"
                      maxDate={new Date()}
                      minTime={getStartOfDay(
                        (pendingCaptureDate || parsedCaptureLessThan) || new Date()
                      )}
                      maxTime={getMaxTime(pendingCaptureDate || parsedCaptureLessThan)}
                      filterTime={(time) =>
                        isSelectableTime(time, pendingCaptureDate || parsedCaptureLessThan)
                      }
                      popperPlacement="bottom-start"
                      popperClassName="capture-date-popper"
                      calendarClassName="capture-date-calendar"
                      wrapperClassName="capture-date-datepicker-wrapper"
                      onKeyDown={(event) => event.preventDefault()}
                      autoComplete="off"
                    />

                    {captureLessThan && (
                      <button
                        type="button"
                        className="capture-date-clear"
                        onClick={() => handleCaptureFilterChange(null)}
                        aria-label="Clear last screenshot capture filter"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {hasActiveFilters && (
                <div
                  className="clr-txt"
                  role="button"
                  tabIndex={0}
                  onClick={handleClearAll}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleClearAll();
                    }
                  }}
                >
                  Clear All
                </div>
              )}
            </div>
          </div>

          {showFilterModal && (
            <div
              className="tablet-filter-modal-overlay"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  handleCloseFilterModal();
                }
              }}
            >
              <div
                className="tablet-filter-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tablet-filter-modal-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="tablet-filter-modal-header">
                  <h5 id="tablet-filter-modal-title">Filter</h5>

                  <button
                    type="button"
                    className="tablet-filter-modal-close"
                    aria-label="Close filter"
                    onClick={handleCloseFilterModal}
                  >
                    ×
                  </button>
                </div>

                <div className="tablet-filter-modal-body">
                  {FILTER_FIELDS.map((field) => (
                    <ModalFilterField
                      key={field.id}
                      label={field.label}
                      placeholder={field.placeholder}
                      options={modalFilters[field.id]}
                      isOpen={modalOpenDropdown === field.id}
                      onToggle={() => handleModalToggleDropdown(field.id)}
                      onToggleOption={(value) =>
                        handleModalToggleOption(field.id, value)
                      }
                      onRemoveChip={(value) =>
                        handleModalRemoveChip(field.id, value)
                      }
                    />
                  ))}

                  <div className="modal-capture-filter">
                    <label className="modal-filter-label">
                      Last Screenshot Capture Less Than
                    </label>

                    <div className="modal-capture-filter-control">

                      <Image
                        src="/images/dashboard/calendar_icon.svg"
                        alt="Calendar"
                        className="modal-capture-calendar-icon"
                        onClick={() => modalCaptureDatePickerRef.current?.setOpen(true)}
                      />

                      <DatePicker
                        ref={modalCaptureDatePickerRef}
                        selected={parsedTempCaptureLessThan}
                        onChange={handleTempCaptureFilterChange}
                        renderCustomHeader={renderDatePickerHeader}
                        showTimeSelect
                        timeIntervals={1}
                        timeCaption="Time"
                        dateFormat="dd-MMM-yyyy h:mm aa"
                        placeholderText="Select Date & Time"
                        maxDate={new Date()}
                        minTime={getStartOfDay(
                          parsedTempCaptureLessThan || new Date()
                        )}
                        maxTime={getMaxTime(parsedTempCaptureLessThan)}
                        filterTime={(time) =>
                          isSelectableTime(time, parsedTempCaptureLessThan)
                        }
                        popperPlacement="bottom-start"
                        popperClassName="capture-date-popper modal-capture-popper"
                        calendarClassName="capture-date-calendar"
                        wrapperClassName="capture-date-datepicker-wrapper"
                        onKeyDown={(event) => event.preventDefault()}
                        autoComplete="off"
                      />

                      {tempCaptureLessThan && (
                        <button
                          type="button"
                          className="modal-capture-clear"
                          onClick={() => setTempCaptureLessThan("")}
                          aria-label="Clear last screenshot capture filter"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {tempCaptureLessThan && (
                      <small>
                        {formatDateTimeLabel(tempCaptureLessThan)}
                      </small>
                    )}
                  </div>
                </div>

                <div className="tablet-filter-modal-footer">
                  <button
                    type="button"
                    className="modal-cancel-btn"
                    onClick={handleCloseFilterModal}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="modal-apply-btn"
                    onClick={handleModalApply}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="sst-table-wrapper">
            <table className="sst-table">
              <thead>
                <tr>
                  <th>Emp ID</th>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>
                    <div className="capture-sort-header">
                      <span>Last Screenshot Capture</span>
                      <Sorter onClick={handleSortCapture} />
                    </div>
                  </th>
                  <th>System Status</th>
                </tr>
              </thead>

              <tbody>
                {rows.length > 0 ? (
                  rows.map((item) => {
                    const statusMeta = getStatusMeta(item.system_status);

                    return (
                      <tr key={item.employee_id}>
                        <td className="emp-id">
                          <span
                            className="table-tooltip-text"
                            title={item.employee_id}
                          >
                            {item.employee_id}
                          </span>
                        </td>

                        <td className="emp-cell">
                          <Image
                            src={
                              item.employee_image ||
                              "/images/dashboard/profile.svg"
                            }
                            alt=""
                            aria-hidden="true"
                            roundedCircle
                            className="emp-avatar"
                          />

                          <div className="emp-cell-text">
                            <div className="emp-name" title={item.employee_name}>
                              {item.employee_name}
                            </div>

                            <div
                              className="emp-email table-tooltip-text"
                              title={item.employee_mail}
                            >
                              {item.employee_mail}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className="table-tooltip-text"
                            title={item.department}
                          >
                            {item.department}
                          </span>
                        </td>

                        <td className="designation-cell">
                          <span
                            className="table-tooltip-text"
                            title={item.designation}
                          >
                            {item.designation}
                          </span>
                        </td>

                        <td className="capture-cell">
                          <div
                            className="capture-time table-tooltip-text"
                            title={item.last_screenshot_capture || "-"}
                          >
                            {item.last_screenshot_capture || "-"}
                          </div>

                          {item.last_screenshot_capture_ago && (
                            <div className="capture-ago">
                              {item.last_screenshot_capture_ago}
                            </div>
                          )}
                        </td>

                        <td>
                          <span className="status-pill">
                            <span className={statusMeta.className} />

                            <span
                              className="table-tooltip-text"
                              title={statusMeta.label}
                            >
                              {statusMeta.label}
                            </span>
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="no-data-row">
                    <td className="no-data" colSpan={6}>
                      <div
                        className="d-flex flex-column justify-content-center align-items-center"
                        style={{ width: "100%", height: "23vh" }}
                      >
                        <Image
                          src="/images/dashboard/user_icon.png"
                          alt="No Data Found"
                          style={{ width: "5rem", height: "5rem" }}
                        />

                        <p className="m-0">No users to show</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>


          {totalElements > 0 && (
            <div className="d-flex justify-content-end mt-3">
              <Pagination
                className="employee-pagination"
                current={current}
                onChange={onPageChange}
                total={totalElements}
                pageSize={PAGE_SIZE}
                showSizeChanger={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MissedScreenshots;